import { useEffect, useRef, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import AuthService from "../services/AuthService";
import DialogBox from "../components/common/DialogBox";
import { useDispatch, useSelector } from "react-redux";
import { setDemandNameMap } from "../redux/actions/demandMap";
import { buildDemandMap } from "../utils/helper";
import useAxios from "../hooks/useAxios";
import useUser from "../hooks/useUser";
import useHelpRequest from "../hooks/useHelpRequest";
import Constants from "../Constants";
import GetHelpButton from "../components/ui-control/GetHelpButton";
import HelpRequestModal from "../components/ui-control/HelpRequestModal";
import NotificationBell from "../components/ui-control/NotificationBell";
import XButton from "../components/common/XButton";
import GlobalBreadcrumb from "../components/common/GlobalBreadcrumb";
import Drawer from "../components/common/Drawer";
import { NotificationProvider } from "../contexts/NotificationContext";
import { BreadcrumbProvider } from "../contexts/BreadcrumbContext";
import { DrawerProvider } from "../contexts/DrawerContext";
import { TourProvider } from "../contexts/TourContext";
import TourReplayAction from "../components/tour/TourReplayAction";
import { cn } from "../utils/classNames";
import NavItem from "./nav/NavItem";
import RenderIf from "../components/render-if/Renderif";
import NavBrand from "./nav/NavBrand";
import { navLinkClasses, navButtonItemClasses } from "./nav/navClasses";
import { contentWrapperClasses } from "../components/common/caseListClasses";

const Home = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate();
    const loggedInUserDetailsSubscriber = useRef(null);
    const [profilePictureImageUrl, setProfilePictureImageUrl] = useState("");
    const [open, setOpen] = useState(false)
    const { isAdmin = false } = useSelector((state) => state.users || {});
    const { getData } = useAxios();
    const { openHelpModal, helpRequestModalProps } = useHelpRequest();

    useUser(true);

    useEffect(() => {
        if (window.location.pathname === '/') {
            navigate('/cases');
        }
        const loggedInUser = AuthService.GetLoggedInUserData();
        setProfilePictureImageUrl(loggedInUser.profileImageUrl);
        registerLoggedInUserDetailsSubscriber();

        return () => {
            loggedInUserDetailsSubscriber.current.unsubscribe();
        }
    }, [dispatch, navigate])

    useEffect(() => {
        const fetchDemandMap = async () => {
            const { success, data } = await getData(Constants.ApiUrl.template.getDemandMap);
            if (success) {
                dispatch(setDemandNameMap(buildDemandMap(data)));
            }
        };
        fetchDemandMap();
    }, [getData, dispatch])

    const registerLoggedInUserDetailsSubscriber = () => {
        loggedInUserDetailsSubscriber.current = AuthService.LoggedInUserDetails.subscribe(value => {
            const imageUrl = value.profileImageUrl;
            setProfilePictureImageUrl(imageUrl);
        })
    }

    return (
        <NotificationProvider>
          <BreadcrumbProvider>
            <DrawerProvider>
              <TourProvider>
                <TopNavBar
                    profileImageUrl={profilePictureImageUrl}
                    setOpen={setOpen}
                    isAdmin={isAdmin}
                    onOpenHelpModal={openHelpModal}
                />
                <div className={contentWrapperClasses}>
                    <Drawer>
                        <GlobalBreadcrumb />
                        <Outlet />
                    </Drawer>
                </div>
                <HelpRequestModal
                    {...helpRequestModalProps}
                    footerAction={<TourReplayAction onReplay={helpRequestModalProps.onClose} />}
                />
                <DialogBox
                    description={'Are you sure want to Logout'}
                    open={open}
                    handleSubmit={() => {
                        AuthService.clearSession();
                        navigate('/account/login');
                    }}
                    handleClose={() => setOpen(false)}
                />
              </TourProvider>
            </DrawerProvider>
          </BreadcrumbProvider>
        </NotificationProvider>
    );
}

// Fixed 72px bar on desktop; height stays auto below lg so the menu can expand.
const navClasses =
    "relative flex flex-wrap items-center justify-between " +
    "py-2 lg:py-0 lg:h-nav-height lg:flex-nowrap lg:justify-start d-navbar";

const navInnerClasses = "flex flex-wrap items-center justify-between container";

const togglerClasses =
    "py-1 px-3 text-xl leading-none bg-transparent " +
    "border border-transparent rounded transition-shadow duration-150 lg:hidden";

const collapseClasses =
    "basis-full grow items-center lg:flex! lg:basis-auto";

const navListClasses =
    "flex flex-col lg:flex-row pl-0 mb-0 list-none " +
    "ml-auto! lg:mb-0!";

// The avatar is a sibling of the link, not part of it: inside, its 40px box
// stretched the active tab's underline half again past the label.
const profileItemClasses = "nav-item profile flex items-center";

const profileImgClasses = "max-w-full h-auto";

const brandWrapperClasses = "flex items-center gap-3";

const NAV_ITEMS = [
   { to: "dashboard", label: "Dashboard" },
   { to: "cases", label: "Cases" },
   { to: "discovery", label: "Discovery", hidden: Constants.isDiscoveryHidden },
   { to: "users", label: "Users", adminOnly: true },
   { to: "template-builder", label: "Template", adminOnly: true },
];


const TopNavBar = ({ profileImageUrl, setOpen, isAdmin, onOpenHelpModal }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    return (
    <nav className={navClasses}>
    <div className={navInnerClasses}>

        <div className={brandWrapperClasses}>
            <NavBrand to='dashboard'/>
            <NotificationBell />
        </div>

        <XButton variant="close" className={togglerClasses}
            onClick={() => setIsMenuOpen(prev => !prev)}
            aria-controls="navbarSupportedContent" aria-expanded={isMenuOpen} aria-label="Toggle navigation">
            <span className="dp-navbar-toggler-icon"></span>
        </XButton>
        <div className={cn(collapseClasses, isMenuOpen ? "flex" : "hidden")} id="navbarSupportedContent">
            <ul className={navListClasses}>

                 {NAV_ITEMS
                    .filter((item) => !item.hidden && (!item.adminOnly || isAdmin))
                    .map(({ to, label }) => (
                        <NavItem key={to} to={to}>{label}</NavItem>
                ))}

                <li className={profileItemClasses}>
                    <NavLink className={navLinkClasses} to="profile">My Profile</NavLink>
                    <RenderIf shouldRender={profileImageUrl}>
                        <img alt="" src={profileImageUrl} className={profileImgClasses} />
                    </RenderIf>
                </li>
                <li className="nav-item">
                    <GetHelpButton
                        variant="navLink"
                        className={navLinkClasses}
                        onClick={onOpenHelpModal}
                    />
                </li>
                <li className={navButtonItemClasses}>
                    <XButton variant="navOutline" onClick={() => setOpen(true)}>
                        Logout
                    </XButton>
                </li>
            </ul>
        </div>
    </div>
    </nav>
    );
};

export default Home;
